// @flow
import isEqual from 'lodash/isEqual';
import merge from 'lodash/merge';
import type { Action } from 'app/common/redux/types';
import type { SummaryBoard } from 'app/common/redux/reducers/board';
import type { SummaryPin } from 'app/common/redux/reducers/pin';
import { FEED_TYPE } from 'app/common/redux/reducers/feeds';

// this is for [R]ead/[U]pdate/[D]elete - anything but create
export const RUD_RESOURCE_MAP = {
  boardactivity: 'BoardActivityResource',
  boardactivitycomment: 'BoardActivityCommentResource',
};

export const BOARD_ACTIVITY_OBJECT_TYPES = {
  BOARD: 'board',
  BOARD_ACTIVITY: 'boardactivity',
  BOARD_ACTIVITY_COMMENT: 'boardactivitycomment',
};

export const PARENT_TO_FEED_TYPE = {
  [BOARD_ACTIVITY_OBJECT_TYPES.BOARD]: FEED_TYPE.BOARD_ACTIVITYITEMS,
  [BOARD_ACTIVITY_OBJECT_TYPES.BOARD_ACTIVITY]: FEED_TYPE.BOARD_ACTIVITYCOMMENTS,
  [BOARD_ACTIVITY_OBJECT_TYPES.BOARD_ACTIVITY_COMMENT]: FEED_TYPE.BOARD_ACTIVITYCOMMENTS,
};

export type BoardActivityItemType = 'boardactivity' | 'boardactivitycomment';
export type ActivityItemType = BoardActivityItemType;

export type BoardActivityType =
  | PCONST.thrift.board_activity.BoardActivityType.PIN
  | PCONST.thrift.board_activity.BoardActivityType.PINS_ADDED
  | PCONST.thrift.board_activity.BoardActivityType.SECTION_CREATED
  | PCONST.thrift.board_activity.BoardActivityType.POST
  | PCONST.thrift.board_activity.BoardActivityType.COLLABORATOR_JOINED
  | PCONST.thrift.board_activity.BoardActivityType.COLLABORATOR_REMOVED
  | PCONST.thrift.board_activity.BoardActivityType.COLLABORATOR_LEFT
  | PCONST.thrift.board_activity.BoardActivityType.SECTION_RENAMED
  | PCONST.thrift.board_activity.BoardActivityType.SECTION_MERGED_TO_SECTION
  | PCONST.thrift.board_activity.BoardActivityType.PIN_MOVED_TO_SECTION
  | PCONST.thrift.board_activity.BoardActivityType.COMMENT;

export type ActivityUser = {
  first_name: string,
  full_name: string,
  id: string,
  image_medium_url: string,
  type: 'user',
  username: string,
};

export type BaseActivityItem = {
  comment_count: number,
  created_at: string,
  content: {
    images: Array<Object>,
    tags: Array<Object>,
    text: string,
  },
  depth?: number,
  id: string,
  reaction_by_me: number,
  reaction_counts: Object,
  tagged_users: Array<Object>,
  user: ActivityUser,
};

export type BoardActivityItem = BaseActivityItem & {
  activity_type: BoardActivityType,
  aggregate_pin_count?: number,
  board: SummaryBoard,
  comment: ?Object,
  pin?: SummaryPin,
  preview_pins?: Array<Object>,
  root_board_activity?: Object,
  ref_board_activity: ?Object,
  type: BoardActivityItemType,
};

export type State = {
  [id: string]: BoardActivityItem,
};

export type ActivityItemReaction = {
  id: string,
  reaction_type: PCONST.thrift.ReactionType.LIKE,
  type: 'userreaction',
  user: ActivityUser,
};

const initialState = {};

// NOTE(yansun): keep the reference the same if the resultant data is the same, this is redux convention
// https://redux.js.org/faq/performance#do-i-have-to-deep-clone-my-state-in-a-reducer-isnt-copying-my-state-going-to-be-slow
const mergeActivityItem = (currentItem, updatedItem) => {
  const mergedItem = {
    ...merge({}, currentItem, updatedItem),
    ...(updatedItem.content ? { content: updatedItem.content } : {}),
    ...(updatedItem.reaction_counts ? { reaction_counts: updatedItem.reaction_counts } : {}),
  };
  return isEqual(currentItem, mergedItem) ? currentItem : mergedItem;
};

// NOTE(jwardle): BoardActivityFeed comment items can contain nested activity items in the comment and
// ref_board_activity fields which should be normalized by id
const normalizeBoardActivityItems = (items: Array<BoardActivityItem>): Array<BoardActivityItem> =>
  items.reduce((normalizedItems, item) => {
    if (
      item.activity_type === PCONST.thrift.board_activity.BoardActivityType.COMMENT &&
      item.comment &&
      item.ref_board_activity
    ) {
      const comment = item.comment;
      const refBoardActivity = item.ref_board_activity;
      if (comment && refBoardActivity) {
        // update the comment in redux to have a full user object, since otherwise it will only be a user id due to join limitations
        normalizedItems.push({ ...comment, user: item.user });
        normalizedItems.push(refBoardActivity);
        normalizedItems.push({
          ...item,
          comment: { id: comment.id },
          ref_board_activity: { id: refBoardActivity.id },
        });
      }
    } else {
      normalizedItems.push(item);
    }
    return normalizedItems;
  }, []);

const activityItemsReducer = (state: State = initialState, action: Action): State => {
  if (action.type === 'RESOURCE_FETCH_COMPLETE' || action.type === 'RESOURCE_FETCH_MORE_COMPLETE') {
    const {
      payload: { data, name, options },
    } = action;
    if (name === 'BoardActivityCommentFeedResource') {
      // NOTE(yansun): might need to deep merge BoardActivityFeedResource and BoardActivityResource data
      //   because the fieldsets can be sufficiently different in the nested objects
      const items = data || [];
      const newItems = items.reduce((map, item) => {
        map[item.id] = mergeActivityItem(state[item.id], item);
        return map;
      }, {});
      return {
        ...state,
        ...newItems,
      };
    } else if (name === 'BoardActivityFeedResource') {
      const items = data || [];
      const normalizedItems = normalizeBoardActivityItems(items);
      const newItems = normalizedItems.reduce((map, item) => {
        map[item.id] = mergeActivityItem(state[item.id], item);
        return map;
      }, {});
      return {
        ...state,
        ...newItems,
      };
    } else if (name === 'BoardActivityResource' && data) {
      const item = data;
      return {
        ...state,
        [item.id]: mergeActivityItem(state[item.id], item),
      };
    } else if (name === 'BoardActivityPinsGridCommentsResource') {
      const items = Object.values(data || {}).reduce((list, value) => list.concat(value || []), []);
      const newItems = items.reduce((map, item: any) => {
        if (Object.prototype.hasOwnProperty.call(item, 'id')) {
          return {
            ...map,
            [item.id]: mergeActivityItem(state[item.id], item),
          };
        }
        return map;
      }, {});
      return {
        ...state,
        ...newItems,
      };
    } else if (name === 'BoardActivityCommentResource' && data) {
      const item = data;
      const newItems = { [item.id]: mergeActivityItem(state[item.id], item) };
      if (item.root_board_activity) {
        const rootId = item.root_board_activity.id;
        newItems[rootId] = mergeActivityItem(state[rootId], item.root_board_activity);
      }
      return {
        ...state,
        ...newItems,
      };
    } else if (
      name === 'NewsHubDetailsResource' &&
      data &&
      data.length > 0 &&
      data[0].content_items
    ) {
      const itemsToAdd = {};
      data.forEach((newsItem) => {
        newsItem.content_items.forEach((contentItem) => {
          const type = contentItem.content_object && contentItem.content_object.type;
          if (
            type === BOARD_ACTIVITY_OBJECT_TYPES.BOARD_ACTIVITY_COMMENT ||
            type === BOARD_ACTIVITY_OBJECT_TYPES.BOARD_ACTIVITY
          ) {
            const item = contentItem.content_object;
            normalizeBoardActivityItems([item]).forEach((item) => (itemsToAdd[item.id] = item));
          }
        });
      });
      return { ...state, ...itemsToAdd };
    } else if (['PinResource', 'PinPageResource'].includes(name) && data && data.board_activity) {
      const item = data.board_activity;
      item.board = data.board;
      return {
        ...state,
        [item.id]: mergeActivityItem(state[item.id], item),
      };
    } else if (
      ((name === 'BoardFeedResource' || name === 'BoardSectionPinsResource') &&
        (options.field_set_key === 'react_grid_pin_with_board_activity' ||
          options.field_set_key === 'partner_react_grid_pin_with_board_activity')) ||
      name === 'BoardActivityPinsFeedResource'
    ) {
      const pins = data || [];
      const newItems = pins.reduce((map, pin) => {
        const activityItem = pin.board_activity;
        if (activityItem && activityItem.id) {
          map[activityItem.id] = mergeActivityItem(state[activityItem.id], activityItem);
        }
        return map;
      }, {});
      return {
        ...state,
        ...newItems,
      };
    }
  } else if (action.type === 'ACTIVITY_ITEM_CREATED') {
    const {
      payload: { activityItem },
    } = action;
    return {
      ...state,
      [activityItem.id]: activityItem,
    };
  } else if (action.type === 'ACTIVITY_ITEM_DELETED') {
    const {
      payload: { activityId, activityType },
    } = action;

    if (
      activityType === BOARD_ACTIVITY_OBJECT_TYPES.BOARD_ACTIVITY_COMMENT ||
      activityType === BOARD_ACTIVITY_OBJECT_TYPES.BOARD_ACTIVITY
    ) {
      const nextState = { ...state };
      delete nextState[activityId];
      return nextState;
    }
  } else if (action.type === 'ACTIVITY_ITEM_UPDATED') {
    const {
      payload: { data },
    } = action;

    return {
      ...state,
      [data.id]: mergeActivityItem(state[data.id], data),
    };
  } else if (action.type === 'UPDATE_ACTIVITY_ITEM_COMMENT_COUNT') {
    const {
      payload: { increment, id },
    } = action;
    const activityItem = state[id];
    if (
      activityItem &&
      [
        BOARD_ACTIVITY_OBJECT_TYPES.BOARD_ACTIVITY_COMMENT,
        BOARD_ACTIVITY_OBJECT_TYPES.BOARD_ACTIVITY,
      ].includes(activityItem.type)
    ) {
      const updatedItem = {
        ...activityItem,
        comment_count: increment ? activityItem.comment_count + 1 : activityItem.comment_count - 1,
      };
      return {
        ...state,
        [id]: updatedItem,
      };
    }
  } else if (action.type === 'ACTIVITY_ITEM_UPDATE_REACTION_COUNT') {
    const {
      payload: { increment, id, reactionType },
    } = action;
    const activityItem = state[id] || {};
    const { reaction_counts: reactionCounts = {} } = activityItem;
    const oldReactionCount = reactionCounts[reactionType] || 0;
    const newReactionCount = increment ? oldReactionCount + 1 : Math.max(oldReactionCount - 1, 0);
    const newReactionByMe = increment ? reactionType : PCONST.thrift.ReactionType.NONE;

    const updatedItem = {
      ...activityItem,
      reaction_counts: {
        ...reactionCounts,
        [reactionType]: newReactionCount,
      },
      reaction_by_me: newReactionByMe,
    };

    return {
      ...state,
      [id]: updatedItem,
    };
  }

  return state;
};

export default activityItemsReducer;
